import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { map, isEmpty } from 'lodash'

import { ReactComponent as IconChevron } from 'assets/icons/IconChevron.svg'
import { MAX_SELECT_HEIGHT } from 'resources'

const Select = ({
  defaultValue,
  handleChange,
  label,
  options,
  nameClass,
  showValidationErrors,
  error,
}) => {
  const selectRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(defaultValue)
  const [isOpenTop, setIsOpenTop] = useState(false)

  const isUuidExist = options?.some((option) => option?.uuid)
  const isDefaultValue = !isEmpty(defaultValue) ?? false

  const displaySelectedValue = (value) => {
    return isUuidExist
      ? options?.find((option) => option?.uuid === value)?.name ?? ''
      : options?.find((option) => option === value) ?? ''
  }

  const onClickSelect = (e) => {
    e.preventDefault()

    // we have to check if select is on the bottom of the screen
    // if so, we have to open it on top
    const windowHeight = window.innerHeight
    const selectPostionToBottom =
      windowHeight - selectRef.current.getBoundingClientRect().bottom

    // MAX_SELECT_HEIGHT is maximum height of select with opened dropdown
    if (selectPostionToBottom < MAX_SELECT_HEIGHT) {
      setIsOpenTop(!isOpenTop)
    }
    setIsOpen(!isOpen)
  }

  const handleClickOutside = (e) => {
    if (selectRef?.current?.contains(e.target)) {
      return
    }
    setIsOpen(false)
    setIsOpenTop(false)
  }

  const onClickOption = (option) => {
    if (option?.uuid === selectedValue || option === selectedValue) {
      setSelectedValue('')

      handleChange(option?.uuid === null ? null : '')
    } else {
      const value = isUuidExist ? option?.uuid : option
      setSelectedValue(value)
      handleChange(value)
    }
    setIsOpen(false)
    setIsOpenTop(false)
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div
      className={classNames('select', nameClass, {
        '-error': showValidationErrors && error !== undefined,
      })}
      ref={selectRef}
    >
      <div
        className={classNames('select__wrapper')}
        onClick={onClickSelect}
      >
        <div
          className={classNames('select__label', {
            '-isSelected': isDefaultValue || selectedValue,
            '-error': showValidationErrors && error !== undefined,
          })}
        >
          {label}
        </div>
        <span className="select__arrowIcon">
          <button
            type="button"
            className={classNames('select__button', {
              '-rotate': isOpen,
            })}
          >
            <IconChevron />
          </button>
        </span>
        <div
          className={classNames('select__selected-value', nameClass, {
            'select__selected-value--is-display ':
              isOpen || isDefaultValue || selectedValue,
          })}
          data-testid="selected-value"
        >
          {isEmpty(selectedValue)
            ? displaySelectedValue(defaultValue)
            : displaySelectedValue(selectedValue)}
        </div>
      </div>
      <ul
        className={classNames('select__options', {
          '-isOpen': isOpen,
          '-is-open-top': isOpenTop,
        })}
      >
        {isEmpty(options) && (
          <li className="select__option -empty">No data available</li>
        )}
        {map(options, (option, idx) => (
          <li
            key={option?.uuid ?? idx}
            className={classNames('select__option', {
              '-active': isEmpty(selectedValue)
                ? option?.uuid === defaultValue ||
                  option === defaultValue
                : option?.uuid === selectedValue ||
                  option === selectedValue,
            })}
            onClick={() => onClickOption(option)}
          >
            {isUuidExist ? option?.name : option}
          </li>
        ))}
      </ul>
    </div>
  )
}

Select.propTypes = {
  defaultValue: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  nameClass: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.string,
}

Select.defaultProps = {
  defaultValue: '',
  label: '',
  nameClass: '',
  options: [],
  handleChange: () => {},
}

export default Select
